// frontend/src/components/PerformanceDashboard.tsx
import { useState, useEffect } from "react";
import { Alert, Button, Chip, Skeleton, Table } from "@heroui/react";
import { BarChart3, Layers, ShieldAlert } from "lucide-react";
import { EmptyState, Panel, Pnl, StatTile, toneOf } from "@/components/kit";
import { cn } from "@/lib/utils";
import { SignalLessons } from "./trades/SignalLessons";
import { closedTradeR, isAutoTrade, type TrackedTrade } from "./desk/deskData";
import { formatR } from "./chart/chartSignals";

interface DrawdownStatus {
  drawdownPct: number;
  /** Your real trades' running total below its best point, in R. */
  drawdownR: number;
  /** Real closed trades it was measured on (practice and engine trades don't count). */
  trades: number;
  tier: string;
  message: string;
  sizeMultiplier: number;
  paused: boolean;
}

interface ScorecardMethod {
  method: string;
  winRate: number;
  avgR: number;
  profitFactor: number;
  trades: number;
}

interface PerformanceMetrics {
  winRate?: number;
  totalTrades?: number;
  wins?: number;
  losses?: number;
  /** Net R per closed engine signal and in total (backend signalResult.ts). */
  avgR?: number;
  netR?: number;
}

interface Factor {
  name: string;
  winRate: number;
  count?: number;
}

interface FactorAnalyticsResponse {
  factors?: Factor[];
}

/** A finished trade from the tracker, with its net R. */
interface ClosedTrade {
  id: string;
  symbol: string;
  r: number;
  closedAt: number;
  /** The engine's own signal (tracked automatically), not one you logged. */
  engine: boolean;
  practice: boolean;
}

/** The last trades that finished, oldest first: engine signals and yours (cancelled-before-entry and watches left out). */
function recentClosed(all: TrackedTrade[], n: number): ClosedTrade[] {
  return all
    .filter(t => t.direction !== "WATCH" && t.status !== "active" && t.status !== "tp1_hit" && !(t.status === "cancelled" && t.exit_price == null))
    .map(t => ({ t, r: closedTradeR(t) }))
    .filter((x): x is { t: TrackedTrade; r: number } => x.r != null && Number.isFinite(x.r))
    .map(({ t, r }) => ({ id: t.id, symbol: t.symbol, r, closedAt: t.closed_at ?? t.created_at, engine: isAutoTrade(t), practice: t.mode === "paper" }))
    .sort((a, b) => a.closedAt - b.closedAt)
    .slice(-n);
}

/** Below this many trades a statistic is not yet meaningful. */
const MIN_SAMPLE = 30;

function SampleFlag({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="num text-muted">n={n}</span>
      {n < MIN_SAMPLE && (
        <Chip size="sm" variant="soft" color="warning">
          Low sample
        </Chip>
      )}
    </span>
  );
}

export function PerformanceDashboard() {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [factors, setFactors] = useState<Factor[]>([]);
  const [recentTrades, setRecentTrades] = useState<ClosedTrade[]>([]);
  const [drawdown, setDrawdown] = useState<DrawdownStatus | null>(null);
  const [scorecard, setScorecard] = useState<ScorecardMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(false);

      const [metricsRes, factorsRes, tradesRes, drawdownRes, scorecardRes] = await Promise.allSettled([
        fetch("/api/performance/metrics"),
        fetch("/api/performance/factor-analytics"),
        fetch("/api/tracked-trades"),
        fetch("/api/drawdown/status"),
        fetch("/api/scorecard"),
      ]);

      if (cancelled) return;

      let anyError = false;

      if (metricsRes.status === "fulfilled" && metricsRes.value.ok) {
        try {
          const body = await metricsRes.value.json();
          // Response shape: { success, metrics: {...}, period }
          setMetrics(body.metrics ?? body);
        } catch {
          anyError = true;
        }
      } else {
        anyError = true;
      }

      if (factorsRes.status === "fulfilled" && factorsRes.value.ok) {
        try {
          const data: FactorAnalyticsResponse = await factorsRes.value.json();
          const sorted = [...(data.factors ?? [])].sort(
            (a, b) => b.winRate - a.winRate
          );
          setFactors(sorted);
        } catch {
          // non-fatal: factors section simply won't render
        }
      }

      if (tradesRes.status === "fulfilled" && tradesRes.value.ok) {
        try {
          const data = await tradesRes.value.json();
          setRecentTrades(recentClosed(data.trades ?? data.data ?? [], 10));
        } catch {
          // non-fatal: bar chart simply won't render
        }
      }

      if (drawdownRes.status === "fulfilled" && drawdownRes.value.ok) {
        try {
          const dd = await drawdownRes.value.json();
          // API shape: { success, state: { drawdownPct, ... }, tier: { tier, multiplier, paused, message } }
          const state = dd.state ?? {};
          const tier = dd.tier ?? {};
          setDrawdown({
            drawdownPct: state.drawdownPct ?? 0,
            drawdownR: state.drawdownR ?? 0,
            trades: state.trades ?? 0,
            tier: tier.tier ?? "normal",
            message: tier.message ?? "",
            sizeMultiplier: tier.multiplier ?? 1,
            paused: tier.paused ?? false,
          });
        } catch {
          // non-fatal
        }
      }

      if (scorecardRes.status === "fulfilled" && scorecardRes.value.ok) {
        try {
          const sc = await scorecardRes.value.json();
          const methods: ScorecardMethod[] = (sc.methods ?? sc.data ?? [])
            .map((m: any) => ({
              method: m.method ?? m.name ?? "unknown",
              winRate: m.winRate ?? 0,
              avgR: m.avgR ?? m.avgRMultiple ?? 0,
              profitFactor: m.profitFactor ?? 0,
              trades: m.trades ?? m.totalTrades ?? 0,
            }))
            .sort((a: ScorecardMethod, b: ScorecardMethod) => b.profitFactor - a.profitFactor);
          setScorecard(methods);
        } catch {
          // non-fatal
        }
      }

      if (anyError) setError(true);
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error && !metrics) {
    return (
      <Alert status="danger" className="mx-auto max-w-3xl">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Failed to load performance data</Alert.Title>
          <Alert.Description>Check your connection and try again.</Alert.Description>
        </Alert.Content>
        <Button size="sm" variant="secondary" onPress={() => setRetryKey((k) => k + 1)}>
          Retry
        </Button>
      </Alert>
    );
  }

  const winRate = metrics?.winRate ?? 0;
  const wins = metrics?.wins ?? 0;
  const losses = metrics?.losses ?? 0;
  const avgR = metrics?.avgR ?? 0;
  const netR = metrics?.netR ?? 0;
  const sampleSize = metrics?.totalTrades ?? wins + losses;

  const topFactor = factors[0] ?? null;
  const top3Factors = factors.slice(0, 3);

  const maxR = recentTrades.reduce((acc, t) => Math.max(acc, Math.abs(t.r)), 0.01);

  return (
    <div className="space-y-4">
      {drawdown && (drawdown.paused || drawdown.tier !== "normal") && (
        <Alert status={drawdown.paused || drawdown.tier !== "caution" ? "danger" : "warning"}>
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>
              {drawdown.paused ? "Trading paused" : `Drawdown tier: ${drawdown.tier}`}
            </Alert.Title>
            <Alert.Description>
              {drawdown.message || "Position size is reduced until drawdown recovers."}{" "}
              <span className="num">Size ×{drawdown.sizeMultiplier.toFixed(2)}</span>
            </Alert.Description>
          </Alert.Content>
        </Alert>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <StatTile
          label="Engine win rate"
          value={sampleSize > 0 ? `${(winRate * 100).toFixed(0)}%` : "—"}
          sub={
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="num">
                {wins}W / {losses}L
              </span>
              <SampleFlag n={sampleSize} />
            </span>
          }
        />
        <StatTile
          label="Engine avg / trade"
          tone={toneOf(avgR)}
          value={sampleSize > 0 ? formatR(avgR) : "—"}
          sub="last 30 days, after fees"
        />
        <StatTile
          label="Engine net result"
          tone={toneOf(netR)}
          value={sampleSize > 0 ? formatR(netR) : "—"}
          sub={<>all closed signals · 1R = the risk to the stop</>}
        />
        <StatTile
          label="Best setup"
          value={
            topFactor ? (
              <span className="block truncate font-sans text-base" title={topFactor.name}>
                {topFactor.name}
              </span>
            ) : (
              <span className="font-sans text-sm font-normal text-muted">No data yet</span>
            )
          }
          sub={
            topFactor ? (
              <span className="flex flex-wrap items-center gap-x-2">
                <span className="num">{(topFactor.winRate * 100).toFixed(0)}% win rate</span>
                {topFactor.count != null && <SampleFlag n={topFactor.count} />}
              </span>
            ) : undefined
          }
        />
        <StatTile
          label="Your drawdown"
          className="col-span-2 md:col-span-1"
          tone={drawdown && drawdown.drawdownPct > 0 ? "loss" : "neutral"}
          value={drawdown ? `${drawdown.drawdownPct.toFixed(1)}%` : "—"}
          sub={
            drawdown ? (
              <span className="capitalize">
                {drawdown.tier} · <span className="num">{drawdown.sizeMultiplier.toFixed(2)}x</span> size
              </span>
            ) : (
              "unavailable"
            )
          }
        />
      </div>

      {/* Why trades won or lost: the reviews, engine signals vs yours */}
      <SignalLessons />

      {/* Chart + risk guard */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          className="lg:col-span-2"
          title="Recent closed trades"
          description={
            recentTrades.length > 0
              ? `Result in R of the last ${recentTrades.length} closed trades, oldest → newest`
              : undefined
          }
        >
          {recentTrades.length > 0 ? (
            <RecentTradesChart trades={recentTrades} maxR={maxR} />
          ) : (
            <EmptyState icon={BarChart3} title="No closed trades" description="Closed trades will appear here." />
          )}
        </Panel>

        <Panel title="Drawdown guard" description="Position sizing tier">
          {drawdown ? (
            <DrawdownDetails drawdown={drawdown} />
          ) : (
            <EmptyState icon={ShieldAlert} title="Drawdown status unavailable" />
          )}
        </Panel>
      </div>

      {/* Breakdown tables */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          flush
          className="lg:col-span-2"
          title="Method scorecard"
          description={`Sorted by profit factor · stats need n≥${MIN_SAMPLE}`}
        >
          {scorecard.length > 0 ? (
            <ScorecardTable methods={scorecard} />
          ) : (
            <EmptyState icon={Layers} title="No scorecard data" />
          )}
        </Panel>

        <Panel flush title="Best performing conditions" description="Top factors by win rate">
          {top3Factors.length > 0 ? (
            <Table variant="secondary" className="rounded-none">
              <Table.ScrollContainer>
                <Table.Content aria-label="Best performing conditions" className="text-[13px]">
                  <Table.Header>
                    <Table.Column isRowHeader>Condition</Table.Column>
                    <Table.Column className="text-right">Win rate</Table.Column>
                  </Table.Header>
                  <Table.Body>
                    {top3Factors.map((factor, i) => (
                      <Table.Row key={factor.name} id={factor.name}>
                        <Table.Cell>
                          <div className="flex items-center gap-2">
                            <span className="num w-4 text-xs text-muted">{i + 1}</span>
                            <div className="min-w-0">
                              <div className="truncate font-medium">{factor.name}</div>
                              {factor.count != null && (
                                <div className="text-xs">
                                  <SampleFlag n={factor.count} />
                                </div>
                              )}
                            </div>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="num text-right">
                          {(factor.winRate * 100).toFixed(0)}%
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
          ) : (
            <EmptyState title="No factor data yet" />
          )}
        </Panel>
      </div>
    </div>
  );
}

function RecentTradesChart({ trades, maxR }: { trades: ClosedTrade[]; maxR: number }) {
  return (
    <div>
      {/* Diverging bars: wins above the zero line, losses below */}
      <div className="flex h-44 gap-2">
        {trades.map((trade) => {
          const isWin = trade.r > 0;
          const heightPct = Math.max(4, Math.round((Math.abs(trade.r) / maxR) * 100));
          const who = trade.engine ? "engine signal" : trade.practice ? "your practice trade" : "your trade";
          const label = `${trade.symbol.replace("USDT", "")}, ${who}: ${formatR(trade.r)}`;
          return (
            <div key={trade.id} className="group flex flex-1 flex-col" title={label} aria-label={label} role="img">
              <div className="flex flex-1 items-end border-b border-separator">
                {isWin && (
                  <div
                    className="w-full rounded-t-sm bg-profit/80 transition-colors group-hover:bg-profit"
                    style={{ height: `${heightPct}%` }}
                  />
                )}
              </div>
              <div className="flex flex-1 items-start">
                {!isWin && (
                  <div
                    className="w-full rounded-b-sm bg-loss/80 transition-colors group-hover:bg-loss"
                    style={{ height: `${heightPct}%` }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex gap-2">
        {trades.map((trade) => (
          <div key={trade.id} className="min-w-0 flex-1 text-center">
            <div className="truncate text-2xs font-medium">{trade.symbol.replace("USDT", "")}</div>
            <Pnl value={trade.r} suffix="R" digits={1} className="block truncate text-2xs" />
            <div className="truncate text-3xs text-muted">{trade.engine ? "engine" : trade.practice ? "practice" : "you"}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-2xs uppercase tracking-wide text-muted">
        <span>older</span>
        <span>recent</span>
      </div>
    </div>
  );
}

function DrawdownDetails({ drawdown }: { drawdown: DrawdownStatus }) {
  const isNormal = drawdown.tier === "normal";
  const isCaution = drawdown.tier === "caution";
  const tierColor = isNormal ? "default" : isCaution ? "warning" : "danger";

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted">Current drawdown</div>
          <div
            className={cn(
              "num mt-1 text-2xl font-semibold",
              drawdown.drawdownPct > 0 ? "text-loss" : "text-foreground"
            )}
          >
            {drawdown.drawdownPct.toFixed(1)}%
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Chip size="sm" variant="soft" color={tierColor} className="capitalize">
            {drawdown.tier}
          </Chip>
          {drawdown.paused && drawdown.tier.toLowerCase() !== "paused" && (
            <Chip size="sm" variant="soft" color="danger">
              PAUSED
            </Chip>
          )}
        </div>
      </div>
      <p className="text-xs leading-relaxed text-muted">
        {drawdown.trades > 0 ? (
          <>
            Your real trades sit <span className="num text-foreground">{drawdown.drawdownR.toFixed(1)}R</span> below their best point,
            measured on <span className="num text-foreground">{drawdown.trades}</span> closed real trade{drawdown.trades === 1 ? "" : "s"}.
          </>
        ) : (
          <>No real trades closed yet.</>
        )}{" "}
        Practice trades and the engine's signals don't count: no money was at risk.
      </p>
      <div className="flex items-center justify-between border-t border-separator pt-3 text-sm">
        <span className="text-muted">Size multiplier</span>
        <span className="num font-semibold">{drawdown.sizeMultiplier.toFixed(2)}x</span>
      </div>
      {drawdown.message && <p className="text-xs text-muted">{drawdown.message}</p>}
    </div>
  );
}

function ScorecardTable({ methods }: { methods: ScorecardMethod[] }) {
  function statusChip(m: ScorecardMethod) {
    if (m.trades < MIN_SAMPLE) {
      return (
        <Chip size="sm" variant="soft" color="warning">
          Data needed
        </Chip>
      );
    }
    if (m.profitFactor > 1.5) {
      return (
        <Chip size="sm" variant="soft" color="success">
          Performing
        </Chip>
      );
    }
    if (m.profitFactor < 0.8) {
      return (
        <Chip size="sm" variant="soft" color="danger">
          Underperforming
        </Chip>
      );
    }
    return (
      <Chip size="sm" variant="soft" color="default">
        Neutral
      </Chip>
    );
  }

  return (
    <Table variant="secondary" className="rounded-none">
      <Table.ScrollContainer className="max-h-[420px] overflow-y-auto scrollbar-thin">
        <Table.Content aria-label="Method scorecard" className="min-w-[560px] text-[13px]">
          <Table.Header className="sticky top-0 z-10 bg-surface">
            <Table.Column isRowHeader>Method</Table.Column>
            <Table.Column className="text-right">Win rate</Table.Column>
            <Table.Column className="text-right">Avg R</Table.Column>
            <Table.Column className="text-right">Profit factor</Table.Column>
            <Table.Column className="text-right">Trades</Table.Column>
            <Table.Column className="text-right">Status</Table.Column>
          </Table.Header>
          <Table.Body>
            {methods.map((m) => {
              const lowSample = m.trades < MIN_SAMPLE;
              return (
                <Table.Row key={m.method} id={m.method}>
                  <Table.Cell className="font-medium capitalize">{m.method}</Table.Cell>
                  <Table.Cell className={cn("num text-right", lowSample && "text-muted")}>
                    {m.winRate.toFixed(0)}%
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    <Pnl value={m.avgR} suffix="R" digits={2} className={cn(lowSample && "opacity-60")} />
                  </Table.Cell>
                  <Table.Cell className={cn("num text-right", lowSample && "text-muted")}>
                    {m.profitFactor.toFixed(2)}
                  </Table.Cell>
                  <Table.Cell className="num text-right text-muted">n={m.trades}</Table.Cell>
                  <Table.Cell className="text-right">{statusChip(m)}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading performance">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border bg-surface px-4 py-3">
            <Skeleton className="h-3 w-16 rounded" />
            <Skeleton className="mt-2 h-6 w-24 rounded" />
            <Skeleton className="mt-2 h-3 w-20 rounded" />
          </div>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Skeleton className="h-64 rounded-lg lg:col-span-2" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
      <Skeleton className="h-56 rounded-lg" />
    </div>
  );
}
